import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, MapPin, Building2, ArrowLeft, Clock, Percent, SlidersHorizontal, X } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import PublicHeader from "@/components/shared/PublicHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const API = process.env.REACT_APP_BACKEND_URL;

const DEAL_IMAGES = [
  "https://images.pexels.com/photos/3845810/pexels-photo-3845810.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  "https://images.pexels.com/photos/4226219/pexels-photo-4226219.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  "https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  "https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  "https://images.pexels.com/photos/4225880/pexels-photo-4225880.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  "https://images.pexels.com/photos/3938022/pexels-photo-3938022.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  "https://images.pexels.com/photos/4226256/pexels-photo-4226256.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
  "https://images.pexels.com/photos/4226269/pexels-photo-4226269.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
];

const TYPE_LABELS = {
  CLINIC: { en: "Clinic", ar: "عيادة" },
  HOSPITAL: { en: "Hospital", ar: "مستشفى" },
  PHARMACY: { en: "Pharmacy", ar: "صيدلية" },
  LAB: { en: "Laboratory", ar: "معمل" },
  RADIOLOGY_CENTER: { en: "Radiology Center", ar: "مركز أشعة" },
  OTHER: { en: "Other", ar: "أخرى" },
};

export default function SearchResultsPage() {
  const { lang, isRTL } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);

  // Filters from URL
  const specialty = searchParams.get("specialty") || "";
  const governorate = searchParams.get("governorate") || "";
  const city = searchParams.get("city") || "";

  // Local filter state (client-side refinement)
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (specialty) params.set("specialty", specialty);
    if (governorate) params.set("governorate", governorate);
    if (city) params.set("city", city);

    fetch(`${API}/api/public/search?${params.toString()}`)
      .then(r => r.json())
      .then(data => { setResults(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [specialty, governorate, city]);

  // Filter deals client-side based on checkbox selections
  const filteredDeals = useMemo(() => {
    if (!results?.deals) return [];
    let deals = results.deals;

    if (selectedTypes.length > 0) {
      deals = deals.filter(d => selectedTypes.includes(d.provider_type));
    }
    if (selectedNeighborhoods.length > 0) {
      deals = deals.filter(d =>
        (d.provider_neighborhoods || []).some(n => selectedNeighborhoods.includes(n))
      );
    }
    if (selectedCategories.length > 0) {
      deals = deals.filter(d =>
        (d.category_ids || []).some(c => selectedCategories.includes(c))
      );
    }
    return deals;
  }, [results, selectedTypes, selectedNeighborhoods, selectedCategories]);

  const filters = results?.filters || { types: [], neighborhoods: [], categories: [] };
  const hasActiveFilters = selectedTypes.length > 0 || selectedNeighborhoods.length > 0 || selectedCategories.length > 0;

  function clearFilters() {
    setSelectedTypes([]);
    setSelectedNeighborhoods([]);
    setSelectedCategories([]);
  }

  function toggleFilter(arr, setArr, value) {
    setArr(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  }

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <PublicHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
        {/* Page Title */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground" data-testid="search-results-title">
              {lang === "ar" ? "العروض المتاحة" : "Available Deals"}
            </h1>
            <Button
              variant="outline"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileFilters(!mobileFilters)}
              data-testid="mobile-filter-btn"
            >
              <SlidersHorizontal className="h-4 w-4 me-1" />
              {lang === "ar" ? "فلاتر" : "Filters"}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {specialty && (
              <span className="bg-secondary text-primary px-3 py-1 rounded-full text-xs font-medium">{specialty}</span>
            )}
            {governorate && (
              <span className="bg-secondary text-primary px-3 py-1 rounded-full text-xs font-medium">{governorate}</span>
            )}
            {city && (
              <span className="bg-secondary text-primary px-3 py-1 rounded-full text-xs font-medium">{city}</span>
            )}
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-xs text-destructive hover:underline flex items-center gap-1">
                <X className="h-3 w-3" /> {lang === "ar" ? "مسح الفلاتر" : "Clear filters"}
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <aside className={`shrink-0 w-64 ${mobileFilters ? 'fixed inset-0 z-50 bg-background p-6 overflow-y-auto' : 'hidden lg:block'}`}>
            {mobileFilters && (
              <div className="flex items-center justify-between mb-6 lg:hidden">
                <h2 className="text-lg font-semibold">{lang === "ar" ? "فلاتر" : "Filters"}</h2>
                <Button variant="ghost" size="sm" onClick={() => setMobileFilters(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            )}

            <div className="space-y-6" data-testid="filter-sidebar">
              {/* Provider Type */}
              {filters.types.length > 0 && (
                <FilterSection
                  title={lang === "ar" ? "نوع مقدم الخدمة" : "Provider Type"}
                  items={filters.types.map(t => ({
                    id: t,
                    label: lang === "ar" ? (TYPE_LABELS[t]?.ar || t) : (TYPE_LABELS[t]?.en || t),
                  }))}
                  selected={selectedTypes}
                  onToggle={(id) => toggleFilter(selectedTypes, setSelectedTypes, id)}
                />
              )}

              {/* Neighborhood */}
              {filters.neighborhoods.length > 0 && (
                <FilterSection
                  title={lang === "ar" ? "المنطقة" : "Neighborhood"}
                  items={filters.neighborhoods.map(n => ({ id: n, label: n }))}
                  selected={selectedNeighborhoods}
                  onToggle={(id) => toggleFilter(selectedNeighborhoods, setSelectedNeighborhoods, id)}
                />
              )}

              {/* Categories (Subcategories) */}
              {filters.categories.length > 0 && (
                <FilterSection
                  title={lang === "ar" ? "التخصص الفرعي" : "Subcategory"}
                  items={filters.categories.filter(c => c.level === 2).map(c => ({
                    id: c.id,
                    label: lang === "ar" ? c.name_ar : c.name_en,
                  }))}
                  selected={selectedCategories}
                  onToggle={(id) => toggleFilter(selectedCategories, setSelectedCategories, id)}
                />
              )}
            </div>

            {mobileFilters && (
              <Button className="w-full mt-6" onClick={() => setMobileFilters(false)}>
                {lang === "ar" ? `عرض ${filteredDeals.length} نتيجة` : `Show ${filteredDeals.length} results`}
              </Button>
            )}
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              </div>
            )}

            {!loading && filteredDeals.length === 0 && (
              <div className="text-center py-20" data-testid="no-results">
                <Search className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-foreground mb-2">
                  {lang === "ar" ? "لم نجد عروض" : "No Deals Found"}
                </h2>
                <p className="text-sm text-muted-foreground mb-6">
                  {hasActiveFilters
                    ? (lang === "ar" ? "حاول تغيير الفلاتر" : "Try adjusting your filters")
                    : (lang === "ar" ? "حاول تغيير التخصص أو المنطقة" : "Try changing the specialty or location")}
                </p>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outline">
                    {lang === "ar" ? "مسح الفلاتر" : "Clear Filters"}
                  </Button>
                )}
              </div>
            )}

            {!loading && filteredDeals.length > 0 && (
              <div data-testid="search-results-list">
                <p className="text-sm text-muted-foreground mb-5">
                  {filteredDeals.length} {lang === "ar" ? "عرض متاح" : "deal(s) available"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {filteredDeals.map((deal, i) => (
                    <DealCard key={deal.id} deal={deal} lang={lang} index={i} onClick={() => navigate(`/deal/${deal.id}`)} />
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function FilterSection({ title, items, selected, onToggle }) {
  const [expanded, setExpanded] = useState(true);
  const displayItems = items.slice(0, expanded ? items.length : 5);

  return (
    <div className="border-b border-border/40 pb-5">
      <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {displayItems.map(item => (
          <label key={item.id} className="flex items-center gap-2.5 cursor-pointer group">
            <Checkbox
              checked={selected.includes(item.id)}
              onCheckedChange={() => onToggle(item.id)}
              className="h-4 w-4"
            />
            <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors truncate">
              {item.label}
            </span>
          </label>
        ))}
      </div>
      {items.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-primary font-medium mt-2 hover:underline"
        >
          {expanded ? "Show less" : `+${items.length - 5} more`}
        </button>
      )}
    </div>
  );
}

function DealCard({ deal, lang, index, onClick }) {
  const discountedPrice = deal.original_price
    ? Math.round(deal.original_price * (1 - deal.discount_value / 100))
    : null;
  const imgSrc = deal.image_url || DEAL_IMAGES[index % DEAL_IMAGES.length];

  return (
    <div
      className="rounded-2xl border border-border/50 bg-white overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer group"
      data-testid={`deal-card-${deal.id}`}
      onClick={onClick}
    >
      <div className="relative h-40 overflow-hidden">
        <img src={imgSrc} alt={deal.title_en} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className="absolute top-3 end-3 bg-primary text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-md">
          <Percent className="h-3 w-3" />
          {deal.discount_value}%
        </div>
        <div className="absolute bottom-3 start-3 flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-white/90 flex items-center justify-center">
            {deal.provider_logo ? (
              <img src={deal.provider_logo} alt="" className="h-4 w-4 object-contain rounded" />
            ) : (
              <Building2 className="h-3.5 w-3.5 text-primary/60" />
            )}
          </div>
          <span className="text-[11px] font-medium text-white drop-shadow-md truncate max-w-[140px]">
            {deal.provider_name}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground leading-snug line-clamp-2 mb-2 group-hover:text-primary transition-colors">
          {lang === "ar" ? deal.title_ar : deal.title_en}
        </h3>
        {deal.original_price && (
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-lg font-bold text-primary">{discountedPrice} <span className="text-xs font-normal">{lang === "ar" ? "ج.م" : "EGP"}</span></span>
            <span className="text-xs text-muted-foreground line-through">{deal.original_price}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-2 border-t border-border/30">
          <div className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            <span className="truncate max-w-[80px]">{(deal.provider_neighborhoods || []).slice(0, 1).join("")}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{deal.end_date?.slice(5)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
